import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import { useQuery, useQueryClient } from 'react-query'
import { AppLayoutContext } from '../../../providers/AppLayout'
import { useAppNavigate } from '../../../utils/url'
import { SnackbarContext } from '../../../providers/SnackbarContext'
import { formatDate } from '../../../utils/formatting'

import { AppPermissions } from '../../../models/AppPermissions.model'

import UserService from '../../../services/UserService/UserService'

import { Button, Grid, Paper, Stack, Typography } from '@mui/material'
import Loading from '../../../components/Loading/Loading.component'
import Breadcrumbs from '../../../components/Breadcrumbs/Breadcrumbs.component'
import FormDialog from '../../../components/FormDialog/FormDialog.component'
import ObstructionTypeForm from '../shared/ObstructionTypeForm.component'
import { AppStateContext } from '../../../providers/AppStateContext'
import { ObstructionType } from '../../../models/ObstructionType.model'
import { ObstructionTypeService } from '../../../services/ObstructionTypeService/ObstructionTypeService'
import ConfirmDeleteDialog from '../../../components/DeleteDialog/ConfirmDeleteDialog.component'
import DetailSection from '../../../components/Sections/DetailSection.component'
import DetailHeader from '../../../components/Sections/DetailHeader.component'
import { useEditSectionParam } from '../../../components/Sections/useEditSectionParam'
import { DetailField, DetailFieldGrid } from '../../../components/Sections/DetailField.component'
import { SectionDefinition } from '../../../components/Sections/Sections.types'
import { ObstructionTypeSectionIds, obstructionTypeSections } from '../shared/ObstructionTypeSections'

const ObstructionTypeDetail = (): JSX.Element => {
  const navigate = useAppNavigate()
  const { id } = useParams<{ id: string }>()
  const [loadingError, setLoadingError] = useState('')

  const { user } = useAuth0()
  const hasWritePermissions = UserService.hasPermissions(user, [AppPermissions.editObstructionType])

  const [isSaving, setIsSaving] = useState(false)
  const { isEditing, editSection, openEditor, closeEditor } = useEditSectionParam()
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false)

  const obstructionTypeService = useMemo(() => new ObstructionTypeService(), []) // memo to prevent re-creating service on every render

  useEffect(() => {
    AppLayoutContext.setPageName(`Obstruction Type`)
  }, [id])

  const queryClient = useQueryClient()
  const { isLoading, data: obstructionType } = useQuery(
    [
      'ObstructionTypeService.get',
      {
        id: Number(id),
      },
      AppStateContext.getRegion(),
    ],
    () =>
      obstructionTypeService.get({
        id: Number(id),
      }),
    {
      onError: (err) => {
        setLoadingError(typeof err === 'string' ? err : 'Failed to load obstruction type.')
        console.error(err)
      },
    },
  )

  const handleEdit = () => {
    closeEditor()
  }

  const handleDeleteClick = () => {
    // show a confirmation dialog before deleting
    setIsDeleteConfirmationOpen(true)
  }

  const handleDelete = () => {
    if (obstructionType?.id) {
      setIsSaving(true)
      setIsDeleteConfirmationOpen(false)
      obstructionTypeService
        .delete(obstructionType.id)
        .then(() => {
          SnackbarContext.show(`Obstruction type deleted successfully. You will now be redirected.`)

          // after a delay of 1 second, redirect to the obstructionType list page
          setTimeout(() => {
            navigate(`/obstruction-type`)
            // invalidate all queries to ensure any queries that are using the modified record are updated
            queryClient.invalidateQueries()
          }, 1000)
        })
        .catch((err) => {
          console.error(err)
          SnackbarContext.show(`Obstruction type failed to delete: ${err}`, 'error')
          setIsSaving(false)
        })
    }
  }

  const sectionById = (sectionId: ObstructionTypeSectionIds) =>
    obstructionTypeSections.find((section) => section.id === sectionId) as SectionDefinition
  // only offer per-section edit shortcuts to users who can edit
  const editSectionHandler = (sectionId: ObstructionTypeSectionIds) =>
    hasWritePermissions ? () => openEditor(sectionId) : undefined

  return (
    <>
      {obstructionType && (
        <FormDialog<ObstructionType>
          open={isEditing}
          entity={obstructionType}
          entityLabel="Obstruction Type"
          onClose={closeEditor}
        >
          <ObstructionTypeForm
            obstructionType={obstructionType}
            initialSection={editSection}
            onEdit={handleEdit}
            onCancel={closeEditor}
          />
        </FormDialog>
      )}
      <ConfirmDeleteDialog
        open={isDeleteConfirmationOpen}
        onClose={() => setIsDeleteConfirmationOpen(false)}
        onDelete={handleDelete}
        title="Confirm Delete"
        content="Are you sure you want to delete this obstruction type?"
      />
      <Paper sx={{ p: 2 }}>
        {isLoading && !loadingError && <Loading sx={{ py: 20 }} />}
        {loadingError && (
          <Stack direction="row" justifyContent="center" alignItems="center" sx={{ py: 20 }}>
            <Typography variant="error">{loadingError}</Typography>
          </Stack>
        )}
        {!isLoading && !loadingError && obstructionType && (
          <>
            {isSaving ? (
              <Loading sx={{ py: 20 }} />
            ) : (
              <>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                  <Breadcrumbs
                    items={() => {
                      const breadcrumbs: Array<{ label: string; href?: string }> = [
                        {
                          label: 'Obstruction Types',
                          href: '/obstruction-type',
                        },
                      ]
                      breadcrumbs.push({ label: `Obstruction Type ID: ${obstructionType.id}` })

                      return breadcrumbs
                    }}
                  />
                  {hasWritePermissions && (
                    <Stack direction="row" justifyContent="end" alignItems="center" spacing={2}>
                      <Button variant="text" onClick={handleDeleteClick}>
                        Delete
                      </Button>
                      <Button variant="contained" onClick={() => openEditor()}>
                        Edit
                      </Button>
                    </Stack>
                  )}
                </Stack>
                <DetailHeader
                  title={obstructionType.name}
                  active={obstructionType.active}
                  subtitle={[obstructionType.region?.code, `ID ${obstructionType.id}`].filter(Boolean).join(' · ')}
                  meta={[`Created ${formatDate(obstructionType.createdAt)}`]}
                />

                <Grid container spacing={2}>
                  {/* --- General ------------------------------------------------- */}
                  <Grid item zero={12} md={6}>
                    <DetailSection
                      section={sectionById(ObstructionTypeSectionIds.general)}
                      onEdit={editSectionHandler(ObstructionTypeSectionIds.general)}
                    >
                      <DetailFieldGrid>
                        <DetailField label="Name">{obstructionType.name || '-'}</DetailField>
                        <DetailField label="Sort Weight">{obstructionType.sortWeight ?? '-'}</DetailField>
                      </DetailFieldGrid>
                    </DetailSection>
                  </Grid>

                  {/* --- Symbol -------------------------------------------------- */}
                  <Grid item zero={12} md={6}>
                    <DetailSection
                      section={sectionById(ObstructionTypeSectionIds.symbol)}
                      onEdit={editSectionHandler(ObstructionTypeSectionIds.symbol)}
                    >
                      <DetailField label="Symbol">{obstructionType.symbol || '-'}</DetailField>
                    </DetailSection>
                  </Grid>
                </Grid>
              </>
            )}
          </>
        )}
      </Paper>
    </>
  )
}
export default ObstructionTypeDetail
